import React, { useEffect, useRef } from 'react';
import { Box, Typography, Container, Grid, Paper } from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import BuildIcon from '@mui/icons-material/Build';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import SettingsRemoteIcon from '@mui/icons-material/SettingsRemote';
import LockIcon from '@mui/icons-material/Lock';

const services = [
  {
    icon: <VideocamIcon sx={{ fontSize: 36 }} />,
    title: 'CCTV Installation',
    desc: 'Full HD & 4K camera installation for homes, offices, shops, and warehouses. Expert placement for maximum coverage.',
    color: '#1e90d4',
  },
  {
    icon: <BuildIcon sx={{ fontSize: 36 }} />,
    title: 'AMC & Maintenance',
    desc: 'Annual Maintenance Contracts for regular servicing, cleaning, and health checks of your surveillance system.',
    color: '#2ecc71',
  },
  {
    icon: <SupportAgentIcon sx={{ fontSize: 36 }} />,
    title: '24/7 Technical Support',
    desc: 'Round-the-clock technical assistance. Quick response time with on-site and remote troubleshooting.',
    color: '#f0c040',
  },
  {
    icon: <CloudDoneIcon sx={{ fontSize: 36 }} />,
    title: 'Cloud Storage & NVR',
    desc: 'Secure cloud-based and local NVR/DVR recording solutions with easy remote access from anywhere.',
    color: '#e74c3c',
  },
  {
    icon: <SettingsRemoteIcon sx={{ fontSize: 36 }} />,
    title: 'Remote Monitoring',
    desc: 'Monitor your property live from your smartphone. Mobile app setup with alerts and notifications.',
    color: '#9b59b6',
  },
  {
    icon: <LockIcon sx={{ fontSize: 36 }} />,
    title: 'Access Control',
    desc: 'Biometric, RFID, and smart lock integrations for complete perimeter security management.',
    color: '#e67e22',
  },
];

export default function Services() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const cards = sectionRef.current?.querySelectorAll('.service-card');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1 }
    );
    cards?.forEach((card, i) => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(40px)';
      card.style.transition = `opacity 0.6s ease ${i * 0.1}s, transform 0.6s ease ${i * 0.1}s`;
      observer.observe(card);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      id="services"
      ref={sectionRef}
      sx={{
        py: 12,
        background: 'linear-gradient(180deg, #081e35 0%, #0a2540 100%)',
        position: 'relative',
      }}
    >
      {/* Background decorative lines */}
      <Box sx={{
        position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute',
          width: '1px', height: '80%', top: '10%',
          left: '20%', background: 'linear-gradient(to bottom, transparent, rgba(30,144,212,0.12), transparent)',
        },
        '&::after': {
          content: '""', position: 'absolute',
          width: '1px', height: '80%', top: '10%',
          right: '20%', background: 'linear-gradient(to bottom, transparent, rgba(30,144,212,0.12), transparent)',
        }
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section header */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography sx={{
            fontSize: '0.8rem', letterSpacing: '3px', color: '#1e90d4',
            textTransform: 'uppercase', mb: 2, fontWeight: 600,
          }}>
            What We Offer
          </Typography>
          <Typography variant="h2" sx={{
            fontSize: { xs: '2.2rem', md: '3rem' },
            color: '#e8f0fe', mb: 2,
          }}>
            Our <Box component="span" sx={{ color: '#1e90d4' }}>Services</Box>
          </Typography>
          <Box sx={{
            width: 60, height: 3,
            background: 'linear-gradient(90deg, #1e90d4, transparent)',
            mx: 'auto', borderRadius: 2,
          }} />
        </Box>

        <Grid container spacing={3}>
          {services.map((service, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Paper
                className="service-card"
                sx={{
                  p: 4,
                  background: 'rgba(13, 40, 71, 0.5)',
                  border: '1px solid rgba(30,144,212,0.12)',
                  borderRadius: '12px',
                  height: '100%',
                  cursor: 'pointer',
                  position: 'relative', overflow: 'hidden',
                  '&:hover': {
                    border: `1px solid ${service.color}40`,
                    transform: 'translateY(-6px)',
                    boxShadow: `0 20px 50px rgba(0,0,0,0.3), 0 0 30px ${service.color}15`,
                    '& .service-icon': {
                      transform: 'scale(1.1)',
                      boxShadow: `0 8px 24px ${service.color}40`,
                    }
                  },
                  transition: 'all 0.3s ease',
                  '&::before': {
                    content: '""', position: 'absolute',
                    top: 0, left: 0, right: 0, height: '3px',
                    background: `linear-gradient(90deg, ${service.color}, transparent)`,
                    opacity: 0, transition: 'opacity 0.3s',
                  },
                  '&:hover::before': { opacity: 1 },
                }}
              >
                <Box
                  className="service-icon"
                  sx={{
                    width: 70, height: 70,
                    background: `${service.color}15`,
                    border: `1px solid ${service.color}30`,
                    borderRadius: '12px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: service.color, mb: 3,
                    transition: 'all 0.3s',
                  }}
                >
                  {service.icon}
                </Box>
                <Typography variant="h6" sx={{ color: '#e8f0fe', fontWeight: 700, mb: 1.5 }}>
                  {service.title}
                </Typography>
                <Typography sx={{ color: '#8fb3d9', lineHeight: 1.7, fontSize: '0.9rem' }}>
                  {service.desc}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
